<template>
  <MainLayout>
    <PageHeader icon="ri-survey-line" title="Encuestas"
      subtitle="Tu opinión nos ayuda a mejorar tu experiencia" :show-button="false" />

    <div class="content-container">
      <form v-if="encuesta" class="encuesta-card" @submit.prevent="enviar" novalidate>
        <header class="encuesta-header">
          <h2>{{ encuesta.titulo }}</h2>
          <p v-if="encuesta.descripcion">{{ encuesta.descripcion }}</p>
          <!-- Texto plano (interpolación escapada, sin v-html) -->
          <p v-if="motivoObligatoriedad" class="encuesta-motivo" role="note">
            <i class="ri-information-line" aria-hidden="true"></i>
            <span>{{ motivoObligatoriedad }}</span>
          </p>
          <p v-if="encuesta.frecuencia_respuesta === 'por_nivel'" class="encuesta-frecuencia">
            Esta encuesta se responde una vez por nivel.
          </p>
        </header>

        <section v-for="(pregunta, index) in encuesta.preguntas" :key="pregunta.id" class="pregunta"
          :class="{ 'pregunta-pendiente': intentoEnvio && esFaltante(pregunta) }">
          <h3 class="pregunta-enunciado" :id="`pregunta-${pregunta.id}`">
            <span class="pregunta-numero">{{ index + 1 }}.</span>
            {{ pregunta.enunciado }}
            <span v-if="pregunta.obligatoria" class="pregunta-obligatoria" title="Obligatoria">*</span>
          </h3>

          <!-- Escala en matriz: una fila por ítem y una columna por valor de la escala -->
          <div v-if="comportamiento(pregunta) === 'escala' && esMatriz(pregunta)" class="matriz-contenedor">
          <!-- Pantallas anchas: tabla -->
          <div class="matriz-scroll">
            <table class="matriz" :aria-labelledby="`pregunta-${pregunta.id}`">
              <thead>
                <tr>
                  <th scope="col" class="matriz-item-cabecera"><span class="sr-only">Ítem</span></th>
                  <th v-for="valor in valoresEscala(pregunta)" :key="valor" scope="col" class="matriz-valor">
                    <span class="matriz-valor-numero">{{ valor }}</span>
                    <span v-if="etiquetaEscala(pregunta, valor)" class="matriz-valor-etiqueta">{{ etiquetaEscala(pregunta, valor) }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in pregunta.opciones" :key="item.id"
                  :class="{ 'fila-pendiente': intentoEnvio && pregunta.obligatoria && respuestas[pregunta.id][item.id] === null }">
                  <th scope="row" class="matriz-item">{{ item.texto }}</th>
                  <td v-for="valor in valoresEscala(pregunta)" :key="valor" class="matriz-celda">
                    <label class="matriz-radio" :class="{ activa: respuestas[pregunta.id][item.id] === valor }">
                      <input type="radio" :name="`pregunta-${pregunta.id}-item-${item.id}`" :value="valor"
                        v-model="respuestas[pregunta.id][item.id]"
                        :aria-label="`${item.texto}: ${valor}${etiquetaEscala(pregunta, valor) ? ' (' + etiquetaEscala(pregunta, valor) + ')' : ''}`" />
                      <span class="matriz-marca" aria-hidden="true"></span>
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Móvil: cada ítem con sus opciones debajo, sin scroll horizontal -->
          <div class="matriz-movil" role="group" :aria-labelledby="`pregunta-${pregunta.id}`">
            <ul v-if="leyendaEscala(pregunta).length" class="matriz-leyenda">
              <li v-for="entrada in leyendaEscala(pregunta)" :key="entrada.valor">
                <strong>{{ entrada.valor }}</strong> = {{ entrada.etiqueta }}
              </li>
            </ul>
            <div v-for="item in pregunta.opciones" :key="item.id" class="matriz-item-movil"
              :class="{ 'fila-pendiente': intentoEnvio && pregunta.obligatoria && respuestas[pregunta.id][item.id] === null }">
              <p class="matriz-item-texto">{{ item.texto }}</p>
              <div class="escala" role="radiogroup" :aria-label="item.texto">
                <label v-for="valor in valoresEscala(pregunta)" :key="valor" class="escala-opcion"
                  :class="{ activa: respuestas[pregunta.id][item.id] === valor }"
                  :title="etiquetaEscala(pregunta, valor) || null">
                  <input type="radio" :name="`pregunta-${pregunta.id}-item-${item.id}-movil`" :value="valor"
                    v-model="respuestas[pregunta.id][item.id]" />
                  <span>{{ valor }}</span>
                </label>
              </div>
            </div>
          </div>
          </div>

          <!-- Escala simple -->
          <div v-else-if="comportamiento(pregunta) === 'escala'" class="escala" role="radiogroup"
            :aria-labelledby="`pregunta-${pregunta.id}`">
            <label v-for="valor in valoresEscala(pregunta)" :key="valor" class="escala-opcion"
              :class="{ activa: respuestas[pregunta.id] === valor }"
              :title="etiquetaEscala(pregunta, valor) || null">
              <input type="radio" :name="`pregunta-${pregunta.id}`" :value="valor"
                v-model="respuestas[pregunta.id]" />
              <span>{{ valor }}</span>
            </label>
          </div>

          <!-- Abierta -->
          <textarea v-else-if="comportamiento(pregunta) === 'abierta'" v-model="respuestas[pregunta.id]"
            class="respuesta-texto" rows="4" maxlength="400" placeholder="Escribe aquí tu respuesta..."
            :aria-labelledby="`pregunta-${pregunta.id}`"></textarea>

          <!-- Selección única -->
          <div v-else-if="comportamiento(pregunta) === 'seleccion_unica'" class="opciones" role="radiogroup"
            :aria-labelledby="`pregunta-${pregunta.id}`">
            <label v-for="opcion in pregunta.opciones" :key="opcion.id" class="opcion"
              :class="{ activa: respuestas[pregunta.id] === opcion.id }">
              <input type="radio" :name="`pregunta-${pregunta.id}`" :value="opcion.id"
                v-model="respuestas[pregunta.id]" />
              <span class="opcion-marca" aria-hidden="true"></span>
              <span class="opcion-texto">{{ opcion.texto }}</span>
            </label>
          </div>

          <!-- Selección múltiple -->
          <div v-else-if="comportamiento(pregunta) === 'seleccion_multiple'" class="opciones" role="group"
            :aria-labelledby="`pregunta-${pregunta.id}`">
            <label v-for="opcion in pregunta.opciones" :key="opcion.id" class="opcion opcion-multiple"
              :class="{ activa: respuestas[pregunta.id].includes(opcion.id) }">
              <input type="checkbox" :value="opcion.id" v-model="respuestas[pregunta.id]" />
              <span class="opcion-marca" aria-hidden="true"></span>
              <span class="opcion-texto">{{ opcion.texto }}</span>
            </label>
          </div>

          <p v-if="intentoEnvio && esFaltante(pregunta)" class="pregunta-error">Esta pregunta es obligatoria.</p>
        </section>

        <footer class="encuesta-footer">
          <p v-if="faltantes.length > 0" class="encuesta-ayuda">
            <i class="ri-information-line"></i>
            Te faltan {{ faltantes.length }} {{ faltantes.length === 1 ? 'pregunta obligatoria' : 'preguntas obligatorias' }} por responder.
          </p>
          <button type="submit" class="btn-enviar" :disabled="enviando">
            Enviar respuestas
            <i class="ri-send-plane-2-line"></i>
          </button>
        </footer>
      </form>
    </div>
  </MainLayout>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import MainLayout from '@/components/Layout/MainLayout.vue';
import PageHeader from '@/components/Layout/PageHeader.vue';

export default {
  name: 'EncuestaSatisfaccionPage',
  components: {
    MainLayout,
    PageHeader
  },
  data() {
    return {
      encuesta: null,
      respuestas: {}, // pregunta_id -> valor (número, texto, id de opción o lista de ids)
      intentoEnvio: false,
      enviando: false
    }
  },
  mounted() {
    this.cargar();
  },
  watch: {
    // Al encadenar otra encuesta obligatoria la ruta cambia solo de :id y el componente se reutiliza
    '$route.params.id'(nuevo, anterior) {
      if (nuevo !== anterior) this.cargar();
    }
  },
  methods: {
    ...mapActions({
      fetchEncuesta: 'encuestaSatisfaccion/fetchEncuesta',
      responder: 'encuestaSatisfaccion/responder',
      fetchPendientes: 'encuestaSatisfaccion/fetchPendientes'
    }),
    ...mapMutations({
      addMensaje: 'auth/addMensaje',
      suspenderBloqueo: 'encuestaSatisfaccion/suspenderBloqueo'
    }),
    cargar() {
      const id = this.$route.params.id;
      this.encuesta = null;
      this.intentoEnvio = false;
      this.fetchEncuesta(id).then(encuesta => {
        // Se inicializan todas las respuestas antes de asignar la encuesta para que sean reactivas
        const respuestas = {};
        (encuesta.preguntas || []).forEach(p => {
          if (this.comportamiento(p) === 'seleccion_multiple') {
            respuestas[p.id] = [];
          } else if (this.esMatriz(p)) {
            // Un valor por ítem (itemId -> valor), todos vacíos al inicio
            respuestas[p.id] = {};
            p.opciones.forEach(item => { respuestas[p.id][item.id] = null; });
          } else {
            respuestas[p.id] = null;
          }
        });
        this.respuestas = respuestas;
        this.encuesta = encuesta;
      }).catch(() => {
        // La encuesta no se pudo cargar (el error ya se muestra). Se reconsulta la lista: si el backend
        // todavía la marca como obligatoria, se suspende el bloqueo para no dejar al estudiante atrapado.
        this.fetchPendientes(true).then(lista => {
          if (lista.some(e => String(e.id) === String(id) && e.obligatoria)) {
            this.suspenderBloqueo(true);
          }
          this.$router.push('/');
        });
      });
    },
    // Una pregunta se comporta según tipo.configuracion.base, o según su código si no la trae
    comportamiento(pregunta) {
      const config = pregunta.tipo && pregunta.tipo.configuracion;
      return (config && config.base) || (pregunta.tipo && pregunta.tipo.codigo);
    },
    // Una escala con ítems (opciones) es una matriz; sin ítems es una escala simple
    esMatriz(pregunta) {
      return this.comportamiento(pregunta) === 'escala' && Array.isArray(pregunta.opciones) && pregunta.opciones.length > 0;
    },
    // Configuración de la escala: config_escala de la pregunta, o la del tipo como respaldo
    configEscala(pregunta) {
      const tipo = (pregunta.tipo && pregunta.tipo.configuracion) || {};
      return { ...tipo, ...(pregunta.config_escala || {}) };
    },
    valoresEscala(pregunta) {
      const config = this.configEscala(pregunta);
      const min = config.min !== undefined ? Number(config.min) : 1;
      const max = config.max !== undefined ? Number(config.max) : 5;
      const paso = Number(config.paso) > 0 ? Number(config.paso) : 1;
      const cantidad = Math.floor((max - min) / paso + 1e-9);
      const valores = [];
      for (let i = 0; i <= cantidad; i++) {
        valores.push(Number((min + i * paso).toFixed(4))); // evita errores de punto flotante con paso 0.5
      }
      return valores;
    },
    // Valores de la escala que tienen etiqueta (para la leyenda de la vista móvil)
    leyendaEscala(pregunta) {
      return this.valoresEscala(pregunta)
        .map(valor => ({ valor, etiqueta: this.etiquetaEscala(pregunta, valor) }))
        .filter(entrada => entrada.etiqueta);
    },
    etiquetaEscala(pregunta, valor) {
      const etiquetas = this.configEscala(pregunta).etiquetas;
      return (etiquetas && etiquetas[String(valor)]) || '';
    },
    estaRespondida(pregunta) {
      const valor = this.respuestas[pregunta.id];
      if (this.esMatriz(pregunta)) return Object.values(valor).some(v => v !== null);
      if (Array.isArray(valor)) return valor.length > 0;
      if (typeof valor === 'string') return valor.trim() !== '';
      return valor !== null && valor !== undefined;
    },
    // En una matriz obligatoria deben responderse todos los ítems
    esFaltante(pregunta) {
      if (!pregunta.obligatoria) return false;
      if (this.esMatriz(pregunta)) return Object.values(this.respuestas[pregunta.id]).some(v => v === null);
      return !this.estaRespondida(pregunta);
    },
    enviar() {
      this.intentoEnvio = true;
      if (this.faltantes.length > 0) {
        const primera = document.getElementById(`pregunta-${this.faltantes[0].id}`);
        if (primera) primera.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const respuestas = this.encuesta.preguntas
        .filter(p => this.estaRespondida(p))
        .map(p => {
          const valor = this.respuestas[p.id];
          if (this.esMatriz(p)) {
            // [{ opcion_id, valor }], un elemento por ítem respondido
            return {
              pregunta_id: p.id,
              valor: p.opciones
                .filter(item => valor[item.id] !== null)
                .map(item => ({ opcion_id: item.id, valor: valor[item.id] }))
            };
          }
          return { pregunta_id: p.id, valor: typeof valor === 'string' ? valor.trim() : valor };
        });

      this.enviando = true;
      this.responder({ id: this.encuesta.id, respuestas }).then(() => {
        this.addMensaje({
          variant: 'success',
          html: '<strong>¡Gracias por responder la encuesta!</strong> Tu opinión nos ayuda a mejorar.',
          autoDismiss: true,
          dismissTime: 8000
        });
        // Se reconsulta: si queda otra obligatoria se continúa con ella; si no, se libera la navegación
        return this.fetchPendientes(true).then(lista => {
          const otra = lista.find(e => e.obligatoria);
          if (otra) {
            this.$router.replace({ name: 'encuesta_satisfaccion', params: { id: otra.id } });
          } else {
            this.$router.push('/');
          }
        });
      }).catch(() => {
        // El error ya se muestra; se conserva lo respondido para poder reintentar
      }).finally(() => {
        this.enviando = false;
      });
    }
  },
  computed: {
    // Solo se muestra si tiene contenido (no null ni vacío)
    motivoObligatoriedad() {
      const motivo = this.encuesta && this.encuesta.motivo_obligatoriedad;
      return typeof motivo === 'string' && motivo.trim() !== '' ? motivo.trim() : '';
    },
    faltantes() {
      if (!this.encuesta) return [];
      return this.encuesta.preguntas.filter(p => this.esFaltante(p));
    }
  }
}
</script>

<style scoped>
.encuesta-card {
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 1.75rem;
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border-color);
  border-radius: 20px;
  box-shadow: var(--theme-shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.encuesta-header h2 {
  margin: 0 0 0.5rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--theme-text-primary);
}

.encuesta-header p {
  margin: 0;
  color: var(--theme-text-secondary);
  line-height: 1.6;
}

.encuesta-header .encuesta-motivo {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: rgba(13, 78, 140, 0.1);
  border-left: 3px solid var(--app-primary-blue);
  border-radius: 8px;
  font-size: 0.9rem;
  white-space: pre-line;
}

.encuesta-header .encuesta-frecuencia {
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: var(--theme-text-muted);
}

.encuesta-motivo i {
  flex-shrink: 0;
  color: var(--app-primary-blue);
}

html.dark-theme .encuesta-motivo i {
  color: var(--app-color-cyan-light);
}

.pregunta {
  padding: 1.25rem;
  background: var(--theme-bg-tertiary);
  border: 1px solid var(--theme-border-color);
  border-radius: 14px;
  transition: border-color 0.2s ease;
}

.pregunta-pendiente {
  border-color: var(--theme-color-danger);
}

.pregunta-enunciado {
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
  color: var(--theme-text-primary);
}

.pregunta-numero {
  color: var(--app-color-cyan-light);
  margin-right: 0.25rem;
}

.pregunta-obligatoria {
  color: var(--theme-color-danger);
  margin-left: 0.15rem;
}

.pregunta-error {
  margin: 0.75rem 0 0;
  font-size: 0.85rem;
  color: var(--theme-color-danger);
}

/* Los inputs nativos se ocultan visualmente pero siguen siendo accesibles por teclado */
.escala-opcion input,
.opcion input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}

/* Escala */
.escala {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.escala-opcion {
  position: relative;
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin: 0;
  background: var(--theme-bg-secondary);
  border: 1px solid var(--theme-border-color-strong);
  border-radius: 12px;
  color: var(--theme-text-secondary);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.escala-opcion:hover {
  border-color: var(--app-primary-blue);
  transform: translateY(-2px);
}

.escala-opcion.activa {
  background: var(--theme-gradient-primary);
  border-color: transparent;
  color: #fff;
  box-shadow: var(--theme-shadow-sm);
}

.escala-opcion:focus-within,
.opcion:focus-within {
  outline: 2px solid var(--app-primary-blue);
  outline-offset: 2px;
}

/* Escala en matriz */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.matriz-scroll {
  overflow-x: auto;
  border: 1px solid var(--theme-border-color);
  border-radius: 12px;
  background: var(--theme-bg-secondary);
}

.matriz {
  width: 100%;
  min-width: 480px;
  border-collapse: collapse;
}

.matriz thead th {
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid var(--theme-border-color);
  text-align: center;
  vertical-align: bottom;
}

.matriz-valor-numero {
  display: block;
  font-weight: 700;
  color: var(--theme-text-primary);
}

.matriz-valor-etiqueta {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1.2;
  color: var(--theme-text-muted);
}

.matriz-item-cabecera {
  width: 40%;
}

.matriz tbody tr + tr th,
.matriz tbody tr + tr td {
  border-top: 1px solid var(--theme-border-color-light);
}

.matriz tbody tr:hover {
  background: var(--theme-hover-bg-light);
}

.matriz tbody tr.fila-pendiente {
  background: rgba(255, 59, 48, 0.08);
}

.matriz-item {
  padding: 0.7rem 1rem;
  text-align: left;
  font-weight: 500;
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--theme-text-primary);
}

.matriz-celda {
  padding: 0.4rem;
  text-align: center;
}

.matriz-radio {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  margin: 0;
  cursor: pointer;
}

.matriz-radio input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
}

.matriz-marca {
  width: 20px;
  height: 20px;
  border: 2px solid var(--theme-border-color-strong);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.matriz-radio:hover .matriz-marca {
  border-color: var(--app-primary-blue);
}

.matriz-radio.activa .matriz-marca {
  border-color: var(--app-primary-blue);
  background: var(--app-primary-blue);
  box-shadow: inset 0 0 0 3px var(--theme-bg-secondary);
}

.matriz-radio:focus-within .matriz-marca {
  outline: 2px solid var(--app-primary-blue);
  outline-offset: 2px;
}

/* Selección única y múltiple */
.opciones {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.opcion {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  padding: 0.75rem 1rem;
  background: var(--theme-bg-secondary);
  border: 1px solid var(--theme-border-color);
  border-radius: 10px;
  color: var(--theme-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.opcion:hover {
  border-color: var(--app-primary-blue);
}

.opcion.activa {
  border-color: var(--app-primary-blue);
  box-shadow: 0 0 0 2px var(--theme-input-focus);
}

.opcion-marca {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: 2px solid var(--theme-border-color-strong);
  border-radius: 50%;
  background: transparent;
  transition: all 0.2s ease;
}

.opcion-multiple .opcion-marca {
  border-radius: 6px;
}

.opcion.activa .opcion-marca {
  border-color: var(--app-primary-blue);
  background: var(--app-primary-blue);
  box-shadow: inset 0 0 0 3px var(--theme-bg-secondary);
}

.opcion-multiple.activa .opcion-marca {
  box-shadow: inset 0 0 0 2px var(--theme-bg-secondary);
}

.opcion-texto {
  line-height: 1.4;
}

/* Abierta */
.respuesta-texto {
  width: 100%;
  min-height: 110px;
  field-sizing: content;
  padding: 0.85rem 0.95rem;
  border: 1px solid var(--theme-input-border);
  border-radius: 10px;
  color: var(--theme-text-primary);
  line-height: 1.55;
  resize: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.respuesta-texto::placeholder {
  color: var(--theme-text-muted);
}

.respuesta-texto:focus {
  outline: none;
  border-color: var(--app-primary-blue);
  box-shadow: 0 0 0 3px var(--theme-input-focus);
}

/* Pie */
.encuesta-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.encuesta-ayuda {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-size: 0.85rem;
  color: var(--theme-text-muted);
}

.btn-enviar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 44px;
  margin-left: auto;
  padding: 0.6rem 1.25rem;
  background: var(--theme-gradient-primary);
  border: 0;
  border-radius: 10px;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  box-shadow: var(--theme-shadow-sm);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.btn-enviar:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--theme-shadow-md);
}

.btn-enviar:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Matriz en móvil: oculta por defecto, se activa en pantallas pequeñas */
.matriz-movil {
  display: none;
}

.matriz-leyenda {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  margin: 0 0 0.25rem;
  padding: 0;
  list-style: none;
  font-size: 0.8rem;
  color: var(--theme-text-muted);
}

.matriz-leyenda strong {
  color: var(--theme-text-primary);
}

.matriz-item-movil {
  padding: 0.85rem;
  background: var(--theme-bg-secondary);
  border: 1px solid var(--theme-border-color);
  border-radius: 12px;
}

.matriz-item-movil.fila-pendiente {
  border-color: var(--theme-color-danger);
  background: rgba(255, 59, 48, 0.08);
}

.matriz-item-texto {
  margin: 0 0 0.65rem;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--theme-text-primary);
}

@media (max-width: 768px) {
  .matriz-scroll {
    display: none;
  }

  .matriz-movil {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* Las opciones se reparten el ancho disponible en lugar de desbordarlo */
  .matriz-item-movil .escala {
    gap: 0.4rem;
  }

  .matriz-item-movil .escala-opcion {
    flex: 1 0 40px;
    width: auto;
    max-width: 56px;
    height: 44px;
  }

  .encuesta-card {
    padding: 1.1rem;
    border-radius: 16px;
  }

  .pregunta {
    padding: 1rem;
  }

  .btn-enviar {
    width: 100%;
  }
}
</style>
